import React, { memo, useMemo } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';

import { Icon } from 'components';
import Avatar from 'components/Avatar';
import { Row } from 'components/layout';
import { Tooltip } from 'components/Tooltip/Tooltip.component';
import { ButtonV3 } from 'components/ComponentV2';
import { reviewOfferModalInstance } from 'components/ReviewOfferModal';
import { useSponsorshipInvite } from 'hooks/useSponsorshipInvite';
import { PAGE_ROUTES } from 'lib/page-routes';

import { SponsorCompanyAttributes } from 'lib/models/sponsor-companies';
import { parseResponse } from 'lib/utils/parser';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { API_ROUTES } from 'lib/api-routes';
import styles from './PendingSponsorshipRequest.module.scss';

export interface PendingSponsorshipRequestProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  invite: SponsorCompanyAttributes;
}

export const PendingSponsorshipRequest: React.FC<PendingSponsorshipRequestProps> =
  memo(
    ({
      className = '', // custom class name
      style, // custom style
      invite,
    }: PendingSponsorshipRequestProps) => {
      const router = useRouter();
      const { updateCompanySeenInvite, mutateSponsorInviteList } =
        useSponsorshipInvite();

      const {
        id: invite_id,
        student_society: studentSocietyResponse,
        kind,
        room_id,
        message_id,
      } = invite;

      const society = parseResponse<StudentSocietyAttributes>(
        studentSocietyResponse
      );

      const { name: societyName, icon_url: iconUrl } = society || {};

      const onReviewClickHandler = async () =>
        await reviewOfferModalInstance({ invite, isSociety: false });

      const updateSeenHandler = async () => {
        try {
          await updateCompanySeenInvite(
            `${API_ROUTES.COMPANY_SPONSORSHIP_INVITE_LIST}/${invite_id}`
          );
          mutateSponsorInviteList();
        } catch (error) {
          console.error(error);
        }
      };

      const renderButton = useMemo(() => {
        if (kind === 'request') {
          return (
            <ButtonV3
              color="secondary"
              size="small"
              onClick={() => onReviewClickHandler()}
            >
              Review
            </ButtonV3>
          );
        } else if (kind === 'conversation') {
          return (
            <ButtonV3
              color="secondary"
              size="small"
              onClick={() => {
                updateSeenHandler();
                router.push(
                  `${PAGE_ROUTES.MESSAGES}?roomId=${room_id}&message_id=${message_id}`
                );
              }}
            >
              View message
            </ButtonV3>
          );
        } else if (kind === 'plan_change_notification') {
          return (
            <ButtonV3
              color="secondary"
              size="small"
              onClick={() => updateSeenHandler()}
            >
              Close
            </ButtonV3>
          );
        }
      }, [invite]);

      const { title, desc, tooltipDesc } = useMemo(() => {
        if (kind === 'request') {
          return {
            title: 'You have new sponsorship request',
            desc: ' added you as a sponsor to their profile.',
            tooltipDesc: `Check out offer from ${societyName} and claim access to all their benefits.`,
          };
        } else if (kind === 'plan_change_notification') {
          return {
            title: 'Sponsorship request has been updated',
            desc: ' has updated their sponsorship request.',
            tooltipDesc: `Check out offer from ${societyName} and claim access to all their benefits.`,
          };
        } else {
          return {
            title: 'You have new sponsorship invitation',
            desc: ' is inviting you to be their sponsor.',
            tooltipDesc:
              'Societies can send you messages to inquire about sponsorship options. Check their profile and message for more details.',
          };
        }
      }, [kind]);

      return (
        <div
          className={cn(
            {
              [styles.PendingNotification]: true,
            },
            className
          )}
          style={style}
          data-testid="PendingSponsorshipRequest"
        >
          <Row
            align="start"
            wrap={false}
            columnGap={8}
            className={styles.header}
          >
            <Icon iconName="info" size="large" />
            <div>
              <p className={cn(styles.title)}>{title}</p>
              <div className={styles.container}>
                <Avatar
                  className={styles.societyLogo}
                  icon={iconUrl}
                  name={`${societyName}`}
                  size="xxsmall"
                />
                <p className={styles.desc}>
                  <mark>{societyName} </mark> {desc}{' '}
                  <Tooltip
                    content={tooltipDesc}
                    color="dark"
                    position="bottom"
                    className={styles.tooltip}
                    containerClassName={styles.tooltipContainer}
                  >
                    Learn more.
                  </Tooltip>
                </p>
              </div>
            </div>
          </Row>
          {renderButton}
        </div>
      );
    }
  );

PendingSponsorshipRequest.displayName = 'PendingSponsorshipRequest';
